import { Button, InfoBar } from "@shapr3d/frontend-lib";

import { useTracker } from "../../../../hooks/useTracker";
import useTranslation from "../../../../hooks/useTranslation";
import { useChargebee } from "../../../../lib/ChargebeeAdapter";
import { ElementClickAction } from "../../../../lib/TrackingAdapter";
import { ServiceErrorCode, SubscriptionPeriod } from "../../../../types";
import { mapChargebeeErrorMessages } from "../../ChargebeeCheckoutSchema";
import { useAllMandatoryFieldsFilled } from "../BillingInfoForm/BillingInfoForm";
import { useCheckoutFormContext } from "../CheckoutFormProvider";
import CouponInput from "../CouponInput/CouponInput";
import MigrationConsent from "../MigrationConsent/MigrationConsent";
import { PriceSummary } from "../PriceSummary/PriceSummary";

import style from "./CheckoutSummary.module.css";

export default function CheckoutSummary() {
  const { t, m, formatDate } = useTranslation();
  const { isLoading } = useChargebee();

  const { submitError, methods, estimateMutationResult, checkout, isMigrationConsentRequired } =
    useCheckoutFormContext();
  const isSubmitting = methods?.formState?.isSubmitting;

  const tracker = useTracker();

  const { data: estimateData, isPending: isEstimatePending } = estimateMutationResult ?? {};

  const hasEstimateValidationError = estimateData?.errors?.some(
    (error) => error.code === ServiceErrorCode.ValidationError
  );

  const allMandatoryFilled = useAllMandatoryFieldsFilled();

  // Only disable for estimate errors when mandatory billing fields are filled.
  // When mandatory fields are still empty, keep the button active so clicking it shows "Required" validation.
  const shouldDisableForEstimateError = hasEstimateValidationError && allMandatoryFilled;

  const isDiscountApplied =
    estimateData?.estimate?.discountAmount !== undefined &&
    Number.parseFloat(estimateData?.estimate.discountAmount) > 0;

  const submitErrorMessages = submitError ? mapChargebeeErrorMessages(submitError) : undefined;
  const submitErrorMessage = submitErrorMessages
    ? {
        title: t(submitErrorMessages.titleMessage),
        description: t(submitErrorMessages.descriptionMessage),
      }
    : undefined;

  const trialEnd = estimateData?.estimate?.trialEnd ?? checkout?.trialEnd;

  const chargedPeriodText =
    estimateData?.estimate?.period === SubscriptionPeriod.Yearly
      ? t(m.Checkout.Summary.Yearly)
      : t(m.Checkout.Summary.Monthly);

  return (
    <div className={style.summary}>
      <div className={style.horizontalLine} />

      <PriceSummary />

      <CouponInput isDiscountApplied={isDiscountApplied} />
      {isMigrationConsentRequired && <MigrationConsent />}
      <Button
        type="submit"
        disabled={isLoading || isSubmitting || isEstimatePending || shouldDisableForEstimateError}
        className={style.submitButton}
        onClick={() => {
          tracker.trackButtonClick({
            elementId: "submit_checkout_form",
            action: trialEnd
              ? ElementClickAction.SUBMIT_CHECKOUT_FORM_TRIAL
              : ElementClickAction.SUBMIT_CHECKOUT_FORM,
            details: "chargebee",
          });
        }}
      >
        {trialEnd ? t(m.Checkout.Summary.StartFreeTrial) : t(m.Checkout.SubscribeNow)}
      </Button>

      {submitErrorMessage && (
        <InfoBar
          className={style.errorBox}
          title={submitErrorMessage.title}
          text={`${submitErrorMessage.description}`}
          variant="critical"
        ></InfoBar>
      )}

      {!isEstimatePending && estimateData && (
        <div className={style.footNote}>
          {!trialEnd && (
            <>
              <span>{t(m.Checkout.Summary.CardChargedOnClick)}</span>
              <br />
            </>
          )}
          {trialEnd && (
            <span>{t(m.Checkout.Summary.TrialEndsOn, { date: formatDate(trialEnd) })}</span>
          )}
          {estimateData.estimate.amountWithSubscriptionMigration && (
            <span>{t(m.Checkout.Summary.SeatsRenewFullPrice)}</span>
          )}
          <span>{t(m.Checkout.Summary.ChargedPeriodically, { period: chargedPeriodText })}</span>
          {estimateData.estimate.currencyCode === "USD" && (
            <span>
              {t(m.Checkout.Summary.AllPricesIn, { currency: estimateData.estimate.currencyCode })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
